import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Typography,
  Box,
  Button,
} from '@mui/material';
import { Form, FormioProvider } from '@formio/react';
import { Formio } from '@formio/js';
import type { RequiredEventForm } from '../../types';
import type { EventFormInfo } from '../../types';
import { parseFormSchema } from '../../utils/formUtils';
import getEventByUuid from '../../hooks/events/getEventByUuid';
import submitEventForm from '../../hooks/user/submitEventForm';
import { eventsRequest } from '../../utils/eventsRequest';

interface FormModalProps {
  open: boolean;
  form: RequiredEventForm | null;
  /** When true, show the form in read-only mode and hide submit actions. */
  readOnly?: boolean;
  onClose: () => void;
  onSubmitted: () => void;
}

const FormModal: React.FC<FormModalProps> = ({ open, form, readOnly = false, onClose, onSubmitted }) => {
  const [eventForm, setEventForm] = useState<EventFormInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submissionData, setSubmissionData] = useState<Record<string, unknown> | null>(null);
  const formRef = useRef<{ submit: () => void } | null>(null);
  const dialogPaperRef = useRef<HTMLDivElement | null>(null);

  const handleClose = () => {
    if (!submitting) {
      setError(null);
      onClose();
    }
  };

  useEffect(() => {
    if (!open || !form) {
      setEventForm(null);
      setError(null);
      setSubmissionData(null);
      return;
    }

    let cancelled = false;
    setSubmissionData(null);

    // Use inline form schema when API returned it
    if (form.form) {
      setEventForm({ id: form.id, name: form.formName, formSchema: form.form });
      setError(null);
    } else if (!form.eventUuid) {
      setEventForm(null);
      setError('Form schema not available.');
    } else {
      setLoading(true);
      setError(null);

      getEventByUuid(form.eventUuid)
        .then((event) => {
          if (cancelled) return;
          if (event?.eventForm) {
            setEventForm(event.eventForm);
          } else {
            setError('Form schema not available for this event.');
          }
        })
        .catch(() => {
          if (!cancelled) {
            setError('Unable to load form. Please try again.');
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }

    if (readOnly && form.submissionId) {
      setLoading(true);
      eventsRequest<any>(`/submissions/${form.submissionId}`, 'get')
        .then((resp) => {
          if (cancelled) return;
          const payload = resp?.data?.data ?? resp?.data;
          const submissionRaw = payload?.submission;
          if (!submissionRaw) return;
          try {
            const parsed = typeof submissionRaw === 'string' ? JSON.parse(submissionRaw) : submissionRaw;
            // forms_api historically stores either:
            // - Form.io submission shape: { data: { ...fields } }
            // - Flat object shape: { fieldKey: value, ... }
            const dataObj =
              parsed && typeof parsed === 'object'
                ? ((parsed as any).data && typeof (parsed as any).data === 'object'
                    ? (parsed as any).data
                    : parsed)
                : null;
            if (dataObj && typeof dataObj === 'object') {
              setSubmissionData(dataObj as Record<string, unknown>);
            }
          } catch {
            // ignore parse errors; still show the schema
          }
        })
        .catch(() => {
          if (!cancelled) {
            setError('Unable to load your submission. Please try again.');
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [open, form?.eventUuid, form?.form, form?.submissionId, readOnly]);

  useEffect(() => {
    if (!open) return;

    document.body.classList.add('events-form-modal-open');

    const paperEl = dialogPaperRef.current;
    const dialogContentEl = paperEl?.querySelector('.MuiDialogContent-root') as HTMLElement | null;

    const getVisibleDropdown = () => {
      const dropdowns = Array.from(document.querySelectorAll('.pac-container, .autocomplete')) as HTMLElement[];
      return (
        dropdowns.find((el) => {
          const style = window.getComputedStyle(el);
          if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
          const rect = el.getBoundingClientRect();
          return rect.width > 0 && rect.height > 0;
        }) ?? null
      );
    };

    const positionDropdownForInput = (inputEl: HTMLInputElement) => {
      const dropdown = getVisibleDropdown();
      if (!dropdown) return;

      const rect = inputEl.getBoundingClientRect();
      const padding = 6;

      dropdown.style.position = 'fixed';
      dropdown.style.left = `${Math.max(padding, rect.left)}px`;
      dropdown.style.width = `${Math.max(240, rect.width)}px`;
      dropdown.style.maxWidth = `calc(100vw - ${padding * 2}px)`;

      // Default to below input.
      let top = rect.bottom + 6;

      // If it would go off-screen, place it above the input instead.
      const dropdownHeight = dropdown.getBoundingClientRect().height || 240;
      if (top + Math.min(dropdownHeight, 240) > window.innerHeight - padding) {
        top = rect.top - 6 - Math.min(dropdownHeight, 240);
      }

      dropdown.style.top = `${Math.max(padding, top)}px`;
      dropdown.style.zIndex = '2000';
    };

    const maybePosition = () => {
      const active = document.activeElement;
      if (!(active instanceof HTMLInputElement)) return;
      if (!paperEl || !paperEl.contains(active)) return;
      if (!active.closest('.address-autocomplete-container')) return;
      positionDropdownForInput(active);
    };

    const onFocusIn = () => {
      // Delay so the provider has time to create/show the dropdown.
      window.setTimeout(maybePosition, 0);
    };

    const onInput = () => {
      // Google/autocomplete providers update the dropdown async; re-position after DOM paint.
      window.setTimeout(maybePosition, 0);
    };

    const dropdownObserver = new MutationObserver(() => {
      // When suggestions update, ensure dropdown stays anchored to the input.
      maybePosition();
    });

    dropdownObserver.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('input', onInput, true);
    window.addEventListener('resize', maybePosition);
    dialogContentEl?.addEventListener('scroll', maybePosition, { passive: true } as any);

    return () => {
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('input', onInput, true);
      window.removeEventListener('resize', maybePosition);
      dialogContentEl?.removeEventListener('scroll', maybePosition as any);
      dropdownObserver.disconnect();
      document.body.classList.remove('events-form-modal-open');
    };
  }, [open]);

  const formDefinition = useMemo(
    () => (eventForm ? parseFormSchema(eventForm.formSchema) : null),
    [eventForm]
  );

  const handleSubmit = async (submission: unknown) => {
    if (!form) return;
    setSubmitting(true);
    try {
      const submittedForChilds = form.childId != null ? [form.childId] : [];
      const result = await submitEventForm(
        form.id,
        submission as { data: Record<string, unknown> },
        form.eventUuid,
        submittedForChilds,
        form.eventId
      );
      if (result.success) {
        onSubmitted();
        onClose();
      } else {
        setError(result.message ?? 'Submit failed. Please try again.');
      }
    } catch {
      setError('Unable to submit form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleError = (err: unknown) => {
    console.error('Form error:', err);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{ className: 'events-form-modal', ref: dialogPaperRef }}
    >
      <DialogTitle>{form?.formName ?? 'Form'}</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Typography variant="body2" color="error" sx={{ py: 2 }}>
            {error}
          </Typography>
        )}
        {!loading && !error && formDefinition && (
          <FormioProvider Formio={Formio} baseUrl="https://form.io" projectUrl="https://form.io">
            <Form
              src={formDefinition as any}
              submission={submissionData ? ({ data: submissionData } as any) : undefined}
              onFormReady={(instance) => {
                formRef.current = instance as { submit: () => void };
              }}
              onSubmit={handleSubmit}
              onError={handleError}
              options={{ readOnly: readOnly || submitting }}
            />
          </FormioProvider>
        )}
      </DialogContent>
      {!readOnly && !loading && !error && formDefinition && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={submitting} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => formRef.current?.submit?.()}
            disabled={submitting}
            sx={{ textTransform: 'none' }}
          >
            {submitting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Submitting…
              </>
            ) : (
              'Submit'
            )}
          </Button>
        </DialogActions>
      )}
      {readOnly && !loading && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default FormModal;
